/* -----------------Helpers & Hooks--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------UI--------------- */
import Box from "@memorang/ui/src/components/Box";
import Loader from "@memorang/ui/src/components/Loader";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import CustomAppbar from "components/CustomAppbar";

import useBreakpoints from "@memorang/ui/src/useBreakpoints";

/* -----------------Types--------------- */
import type { MixedItemDist } from "@memorang/types/src/tag";

/* -----------------Child components--------------- */
import { PracticeTestsView } from "../components/PracticeTestsView";

import { appBarMeta } from "@constants/appbar";
import { isWeb } from "@helpers/platform";
/* -----------------Helpers--------------- */
import { getPracticeTestsData } from "../helpers/practice-tests-data";

import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import { useAppStore } from "@hooks/useAppStore";
import { useDevice } from "@hooks/useDevice";
import { ErrorDialog } from "components/ErrorDialog";
import { OLXExamEligibilityDialog } from "../../../components/OLXExamEligibilityDialog";
/* -----------------Routing--------------- */
import { PurchaseDialog } from "../components/PurchaseDialog";
import { usePracticeTests } from "../hooks/usePracticeTests";

const PracticeTestsContainer = () => {
	const { mixedItemDistResponse } = usePracticeTests();

	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const { smDown } = useBreakpoints();
	const finalHeaderTitle = smDown
		? ((examDate || practiceTestLabel) ?? "Practice Tests")
		: appBarMeta["practice-tests"].title(practiceTestLabel);
	const finalSubtitle = smDown
		? ""
		: appBarMeta["practice-tests"].description(practiceTestLabel);

	const CustomLoader = () => {
		return (
			<Box flex={1}>
				<CustomAppbar
					back
					options={{
						headerTitle: finalHeaderTitle,
						subtitle: finalSubtitle,
					}}
					role="banner"
				/>
				<Loader />
			</Box>
		);
	};
	if (!mixedItemDistResponse) {
		return <CustomLoader />;
	}

	return (
		<PreloadedView
			mixedItemDistResponse={mixedItemDistResponse}
			headerTitle={finalHeaderTitle}
			headerSubtitle={finalSubtitle}
		/>
	);
};

export default PracticeTestsContainer;

const PreloadedView = ({
	mixedItemDistResponse,
	headerTitle,
	headerSubtitle,
}: {
	mixedItemDistResponse: MixedItemDist;
	headerTitle: string;
	headerSubtitle: string;
}) => {
	const {
		currentExamContext: { examName, examDate },
	} = useExamContext();

	const data = mixedItemDistResponse;
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const {
		sections,
		stats,
		hasAccess: hasProductAccess,
	} = getPracticeTestsData(data, examName, examDate, diagnosticLabel);

	const {
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		showErrorDialog,
		setShowErrorDialog,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
	} = usePracticeTests();

	const Wrapper = isWeb ? ScrollContainer : Box;
	const { isMobile } = useDevice();

	return (
		<>
			<CustomAppbar
				back={isWeb ? true : !isMobile}
				options={{
					headerTitle,
					subtitle: headerSubtitle,
				}}
				stats={stats}
				role="banner"
			/>
			<Wrapper
				style={{
					flexGrow: 1,
					paddingVertical: 24,
				}}
				// biome-ignore lint/a11y/useSemanticElements: <explanation>
				role="main"
			>
				<PracticeTestsView
					sections={sections}
					handleNavigation={handlePracticeTestSession}
					selectedExamId={selectedExamId}
					disableCard={creatingSession}
					hasProductAccess={hasProductAccess}
				/>
				{showPurchaseDialog && (
					<PurchaseDialog
						visible={showPurchaseDialog}
						handleClose={toggleShowPurchaseDialog}
						productType="PRACTICE_TEST"
						fromPracticeTestPage
					/>
				)}
				{showErrorDialog && (
					<ErrorDialog
						visible={showErrorDialog}
						handleClose={() => setShowErrorDialog(false)}
						title="Error"
						message="Session creation failed. Please try again."
					/>
				)}
				{!!olxError && (
					<OLXExamEligibilityDialog
						title={olxError.title}
						message={olxError.message}
						visible={!!olxError}
						handleClose={() => setOlxError(null)}
					/>
				)}

				{showCollectUserDetailsDialog && (
					<CollectUserDetailsDialog
						visible={showCollectUserDetailsDialog}
						handleClose={toggleShowCollectUserDetailsDialog}
					/>
				)}
			</Wrapper>
		</>
	);
};
